import { useState } from "react"
import { Form, Input, Button, Typography, Alert, Space, message } from "antd"
import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
  DownOutlined,
  RightOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons"

const { Title, Text, Link } = Typography

export default function FreshbooksForm({ onBack }) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [optionalFieldsOpen, setOptionalFieldsOpen] = useState(false)

  const handleSubmit = async (values) => {
    setLoading(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500))
      console.log("Form submitted with values:", values)
      message.success({
        content: "FreshBooks source created successfully!",
        icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      })
      form.resetFields()
      setOptionalFieldsOpen(false) // Close optional fields on success
    } catch (error) {
      message.error("Failed to create source. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitFailed = (errorInfo) => {
    message.error("Please fix the errors in the form before submitting.")
    console.log("Form validation failed:", errorInfo)
  }

  const validateUrl = (_, value) => {
    if (!value) return Promise.resolve()
    try {
      new URL(value)
      return Promise.resolve()
    } catch {
      return Promise.reject(new Error("Please enter a valid URL"))
    }
  }

  const validateUuid = (_, value) => {
    if (!value) return Promise.resolve()
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    if (!uuidRegex.test(value)) {
      return Promise.reject(new Error("Please enter a valid UUID format"))
    }
    return Promise.resolve()
  }

  const validateTimeout = (_, value) => {
    if (!value) return Promise.resolve()
    if (!/^\d+$/.test(value)) {
      return Promise.reject(new Error("Timeout must be a positive number"))
    }
    return Promise.resolve()
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        padding: "48px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Back button */}
      {onBack && (
        <Button
          type="text"
          onClick={onBack}
          style={{
            position: "absolute",
            top: "24px",
            left: "24px",
            color: "#6b7280", // Gray text
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            borderRadius: "8px",
          }}
        >
          <ArrowLeftOutlined style={{ marginRight: "8px" }} />
          Back
        </Button>
      )}

      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "32px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={2} style={{ margin: 0, color: "#1f2937", fontSize: "28px" }}>
              Create a FreshBooks Source
            </Title>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#2563eb",
                color: "white",
                padding: "6px 16px",
                borderRadius: "9999px",
                fontSize: "13px",
                fontWeight: "600",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "white",
                  borderRadius: "9999px",
                  marginRight: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563eb",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                F
              </div>
              FreshBooks
              <span style={{ marginLeft: "8px", fontSize: "11px", opacity: 0.8 }}>MARKETPLACE</span>
            </div>
          </div>
          <Text style={{ color: "#4b5563", fontSize: "16px" }}>
            Connect your FreshBooks account to seamlessly sync your data.
          </Text>
        </div>

        <div style={{ padding: "32px" }}>
          <Alert
            message={
              <span style={{ display: "flex", alignItems: "flex-start" }}>
                <InfoCircleOutlined style={{ marginRight: "12px", marginTop: "2px", color: "#2563eb" }} />
                <div>
                  <Text strong style={{ color: "#1e40af" }}>
                    Community Maintained Connector
                  </Text>
                  <br />
                  <Text style={{ color: "#1e40af" }}>
                    This connector is maintained by the Airbyte community. Airbyte does not offer support SLAs. Please
                    refer to our{" "}
                    <Link href="#" style={{ color: "#2563eb", textDecoration: "underline" }}>
                      documentation
                    </Link>{" "}
                    for more details.
                  </Text>
                </div>
              </span>
            }
            type="info"
            showIcon={false}
            style={{
              backgroundColor: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "8px",
              marginBottom: "32px",
              padding: "16px",
            }}
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            autoComplete="off"
            initialValues={{ sourceName: "FreshBooks" }}
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "24px" }}>
              {[
                { name: "sourceName", label: "Source name", required: true, placeholder: "e.g., My FreshBooks Data" },
                { name: "clientId", label: "Client ID", required: true, placeholder: "Your FreshBooks Client ID" },
                {
                  name: "clientSecret",
                  label: "Client secret",
                  required: true,
                  placeholder: "Your FreshBooks Client Secret",
                  type: "password",
                },
                {
                  name: "redirectUri",
                  label: "Redirect URI",
                  required: true,
                  validator: validateUrl,
                  placeholder: "https://your-app.com/oauth/callback",
                },
                {
                  name: "accountId",
                  label: "Account ID",
                  required: true,
                  placeholder: "Your FreshBooks Account ID",
                  type: "password",
                },
                {
                  name: "refreshToken",
                  label: "Refresh token",
                  required: true,
                  placeholder: "Your FreshBooks Refresh Token",
                  type: "password",
                },
                {
                  name: "businessUuid",
                  label: "Business UUID",
                  required: true,
                  validator: validateUuid,
                  placeholder: "e.g., 123e4567-e89b-12d3-a456-426614174000",
                },
              ].map((field) => (
                <Form.Item
                  key={field.name}
                  name={field.name}
                  label={
                    <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                      {field.label} {field.required && <span style={{ color: "#ef4444" }}>*</span>}
                    </Text>
                  }
                  rules={[
                    field.required && { required: true, message: `${field.label} is required` },
                    field.validator && { validator: field.validator },
                  ].filter(Boolean)}
                  style={{ marginBottom: 0 }}
                >
                  {field.type === "password" ? (
                    <Input.Password
                      placeholder={field.placeholder}
                      style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                      autoComplete="new-password"
                    />
                  ) : (
                    <Input
                      placeholder={field.placeholder}
                      style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                      autoComplete="off"
                    />
                  )}
                </Form.Item>
              ))}
            </div>

            <div style={{ paddingTop: "16px" }}>
              <Button
                type="text"
                onClick={() => setOptionalFieldsOpen(!optionalFieldsOpen)}
                style={{
                  padding: "8px 0",
                  color: "#374151",
                  fontSize: "16px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {optionalFieldsOpen ? (
                  <DownOutlined style={{ marginRight: "8px", fontSize: "14px" }} />
                ) : (
                  <RightOutlined style={{ marginRight: "8px", fontSize: "14px" }} />
                )}
                Advanced Configuration (Optional)
              </Button>
            </div>

            {optionalFieldsOpen && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "24px",
                  padding: "24px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                {[
                  { name: "apiVersion", label: "API Version", placeholder: "e.g., v1" },
                  { name: "timeout", label: "Timeout (seconds)", placeholder: "e.g., 60", validator: validateTimeout },
                ].map((field) => (
                  <Form.Item
                    key={field.name}
                    name={field.name}
                    label={<Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>{field.label}</Text>}
                    rules={[field.validator && { validator: field.validator }].filter(Boolean)}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder={field.placeholder}
                      style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                      autoComplete="off"
                    />
                  </Form.Item>
                ))}
              </div>
            )}

            {/* Submit + Reset */}
            <Form.Item style={{ marginTop: "32px", marginBottom: 0, textAlign: "right" }}>
              <Space size="middle">
                <Button
                  size="medium"
                  onClick={() => {
                    form.resetFields()
                    setOptionalFieldsOpen(false)
                  }}
                  style={{
                    borderRadius: "6px",
                    fontSize: "16px",
                    padding: "0 24px",
                    borderColor: "#d1d5db", // Gray-300
                    color: "#374151", // Gray-700
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="medium"
                  style={{
                    backgroundColor: "#2563eb", // Blue-600
                    borderColor: "#2563eb",
                    borderRadius: "6px",
                    fontSize: "16px",
                    padding: "0 24px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  {loading ? "Creating..." : "Create Source"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  )
}
